import type { AdminRouterContext } from '@/lib/router-context'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import SaveBar from '@/components/forms/SaveBar'
import { useIsReadOnly, type AppAbility } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import {
  metaPixelQueries,
  useRemoveMetaPixelIntegrationMutation,
  useSuspenseMetaPixelIntegration,
  useUpdateMetaPixelIntegrationMutation,
} from '@/lib/services/meta-pixel/meta-pixel.query'
import {
  metaPixelIntegrationSchema,
  normalizeMetaPixelId,
} from '@/lib/services/meta-pixel/meta-pixel.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { ExternalLink, Target, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

function canViewMetaPixel(ability: Pick<AppAbility, 'can'>) {
  return ability.can('list', 'meta-pixel')
}

function canEditMetaPixel(ability: Pick<AppAbility, 'can'>) {
  return ability.can('edit', 'meta-pixel')
}

export async function metaPixelSettingsLoader({
  context,
  params,
}: {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: AdminRouterContext['queryClient']
  }
  params: { orgId: string | number }
}): Promise<{ isReadOnly: boolean }> {
  if (!canViewMetaPixel(context.ability)) throw notFound()

  const isReadOnly = !canEditMetaPixel(context.ability)
  const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

  if (isGlobalOrg) return { isReadOnly }

  await context.queryClient.ensureQueryData(
    metaPixelQueries.integration({ organizationId: params.orgId })
  )

  return { isReadOnly }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/integrations/meta-pixel/')({
  component: MetaPixelSettingsPage,
  loader: metaPixelSettingsLoader,
})

function OrganizationMetaPixelSettings() {
  const orgId = useRouteOrganizationIdParam()
  const { isReadOnly } = useIsReadOnly()
  const { data: integration } = useSuspenseMetaPixelIntegration({ organizationId: orgId })
  const updateMutation = useUpdateMetaPixelIntegrationMutation()
  const removeMutation = useRemoveMetaPixelIntegrationMutation()
  const form = useAppForm({
    defaultValues: {
      pixel_id: integration.pixel_id ?? '',
    },
    validators: {
      onSubmit: metaPixelIntegrationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const savedIntegration = await updateMutation.mutateAsync({
            pixel_id: normalizeMetaPixelId(value.pixel_id),
          })
          const savedValues = { pixel_id: savedIntegration.pixel_id ?? '' }

          toast.success('Meta Pixel connected')
          setTimeout(() => formApi.reset(savedValues))
          return undefined
        } catch (error) {
          toast.error('Could not save Meta Pixel', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  async function removeIntegration() {
    try {
      await removeMutation.mutateAsync()
      form.reset({ pixel_id: '' })
      toast.success('Meta Pixel disconnected')
    } catch (error) {
      toast.error('Could not disconnect Meta Pixel', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <AdminPageHeader
        icon={Target}
        title="Meta Pixel"
        description="Send visits and shopping activity from your live website to your Meta Pixel."
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Meta Pixel</CardTitle>
            <Badge variant={integration.connected ? 'success' : 'outline'}>
              {integration.connected ? 'Connected' : 'Not connected'}
            </Badge>
          </div>
          <CardDescription>
            Enter the numeric Pixel ID from your Meta Events Manager data source.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form.AppForm>
            <form.Form>
              <div className="space-y-5">
                <form.AppField name="pixel_id">
                  {(field) => (
                    <field.TextInput
                      label="Pixel ID"
                      placeholder="000000000000000"
                      inputMode="numeric"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  )}
                </form.AppField>

                <div className="rounded-lg border border-border/60 bg-muted/25 p-4">
                  <Typography variant="label" className="block">
                    What your account will receive
                  </Typography>
                  <Typography variant="supporting" className="mt-1 block">
                    Page views and standard shopping events from the live storefront help you
                    measure ads, understand conversions, and build website audiences. Website
                    previews are not tracked.
                  </Typography>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  <a
                    href="https://www.facebook.com/business/help/952192354843755"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                  >
                    Find your Pixel ID
                    <ExternalLink className="size-3.5" />
                  </a>

                  {!isReadOnly ? (
                    <div className="ml-auto flex items-center gap-2">
                      {integration.connected ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              disabled={removeMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect Meta Pixel?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Your live website will stop sending new page views and shopping
                                events to this Meta Pixel. Existing Meta reports and audiences will
                                not be deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button variant="destructive" onClick={removeIntegration}>
                                  Disconnect
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : null}
                      <SaveBar
                        mutation={updateMutation}
                        layout="inline"
                        renderInPortal={false}
                        submitButtonText={integration.connected ? 'Save' : 'Connect'}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </form.Form>
          </form.AppForm>
        </CardContent>
      </Card>
    </div>
  )
}

export function MetaPixelSettingsPage() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <div className="max-w-3xl space-y-5">
          <AdminPageHeader
            icon={Target}
            title="Meta Pixel"
            description="Send visits and shopping activity from your live website to your Meta Pixel."
          />
          <OrganizationContextNotice>
            Switch to the florist account that owns the Meta Pixel connection.
          </OrganizationContextNotice>
        </div>
      }
    >
      <OrganizationMetaPixelSettings />
    </IfGlobalOrg>
  )
}
